import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  CircularProgress,
  Typography,
  Box,
  Alert,
  Tabs,
  Tab,
} from '@mui/material';
import { adminApi } from '../services/adminApi';
import { AccountDeletionRequest } from '../types';
import { format } from 'date-fns';

export function AccountDeletionList() {
  const queryClient = useQueryClient();
  const [selectedRequest, setSelectedRequest] = useState<AccountDeletionRequest | null>(null);
  const [editedRequest, setEditedRequest] = useState<Partial<AccountDeletionRequest>>({});
  const [statusFilter, setStatusFilter] = useState<string>('all');

  const { data: requests, isLoading, error } = useQuery({
    queryKey: ['accountDeletions', statusFilter],
    queryFn: () => adminApi.getAccountDeletions(statusFilter),
    refetchInterval: 30000,
  });

  const updateMutation = useMutation({
    mutationFn: (request: Partial<AccountDeletionRequest>) => 
      adminApi.updateAccountDeletion(request),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['accountDeletions'] });
      setSelectedRequest(null);
      setEditedRequest({});
    },
  });

  const handleReview = (request: AccountDeletionRequest) => {
    setSelectedRequest(request);
    setEditedRequest({
      id: request.id,
      status: request.status,
      admin_notes: request.admin_notes || '',
    });
  };

  const handleSave = () => {
    if (editedRequest.id) {
      updateMutation.mutate({
        ...editedRequest,
        reviewed_at: new Date().toISOString(),
      });
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'pending': return 'warning';
      case 'in_review': return 'info';
      case 'completed': return 'success';
      case 'cancelled': return 'default';
      default: return 'default';
    }
  };

  const filteredRequests = requests?.filter((req: AccountDeletionRequest) => {
    if (statusFilter === 'all') return true;
    return req.status === statusFilter;
  });

  const statusCounts = {
    all: requests?.length || 0,
    pending: requests?.filter((r: AccountDeletionRequest) => r.status === 'pending').length || 0,
    in_review: requests?.filter((r: AccountDeletionRequest) => r.status === 'in_review').length || 0,
    completed: requests?.filter((r: AccountDeletionRequest) => r.status === 'completed').length || 0,
    cancelled: requests?.filter((r: AccountDeletionRequest) => r.status === 'cancelled').length || 0,
  };

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return <Alert severity="error">Failed to load account deletion requests: {String(error)}</Alert>;
  }

  return (
    <>
      <Typography variant="h5" gutterBottom>
        Account Deletion Requests ({statusCounts.all})
      </Typography>
      <Typography variant="body2" color="text.secondary" paragraph>
        Review and process user account deletion requests (GDPR/CCPA compliance)
      </Typography>

      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
        <Tabs value={statusFilter} onChange={(_, value) => setStatusFilter(value)}>
          <Tab label={`All (${statusCounts.all})`} value="all" />
          <Tab 
            label={`Pending (${statusCounts.pending})`} 
            value="pending"
            sx={statusCounts.pending > 0 ? { color: 'warning.main' } : {}}
          />
          <Tab label={`In Review (${statusCounts.in_review})`} value="in_review" />
          <Tab label={`Completed (${statusCounts.completed})`} value="completed" />
          <Tab label={`Cancelled (${statusCounts.cancelled})`} value="cancelled" />
        </Tabs>
      </Box>

      <TableContainer>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Requested</TableCell>
              <TableCell>Email</TableCell>
              <TableCell>User ID</TableCell>
              <TableCell>Reason</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Reviewed By</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredRequests?.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} align="center">
                  <Typography color="text.secondary" py={3}>
                    No {statusFilter !== 'all' ? statusFilter : ''} deletion requests found
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              filteredRequests?.map((request: AccountDeletionRequest) => (
                <TableRow key={request.id} hover>
                  <TableCell>
                    <Box>
                      <Typography variant="body2">
                        {format(new Date(request.requested_at), 'MMM d, yyyy')}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {format(new Date(request.requested_at), 'h:mm a')}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <strong>{request.email}</strong>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>
                      {request.user_id ? request.user_id.substring(0, 8) + '...' : 'Website'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" sx={{ maxWidth: 200 }}>
                      {request.reason || <em style={{ color: '#999' }}>No reason provided</em>}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={request.status.replace('_', ' ')}
                      color={getStatusColor(request.status) as any}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    {request.reviewed_by ? (
                      <Box>
                        <Typography variant="body2">{request.reviewed_by}</Typography>
                        {request.reviewed_at && (
                          <Typography variant="caption" color="text.secondary">
                            {format(new Date(request.reviewed_at), 'MMM d')}
                          </Typography>
                        )}
                      </Box>
                    ) : (
                      '-'
                    )}
                  </TableCell>
                  <TableCell>
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={() => handleReview(request)}
                    >
                      Review
                    </Button>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog
        open={!!selectedRequest}
        onClose={() => setSelectedRequest(null)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Review Account Deletion Request</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Alert severity="info">
              Review this request within 30 days as required by GDPR/CCPA regulations.
            </Alert>

            <Box>
              <Typography variant="subtitle2" color="text.secondary">Request Details</Typography>
              <Box sx={{ mt: 1, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                <Typography variant="body2"><strong>Email:</strong> {selectedRequest?.email}</Typography>
                <Typography variant="body2"><strong>User ID:</strong> {selectedRequest?.user_id || 'Website request'}</Typography>
                <Typography variant="body2"><strong>Requested:</strong> {selectedRequest?.requested_at && format(new Date(selectedRequest.requested_at), 'PPpp')}</Typography>
                {selectedRequest?.reason && (
                  <Typography variant="body2" sx={{ mt: 1 }}>
                    <strong>Reason:</strong> {selectedRequest.reason}
                  </Typography>
                )}
              </Box>
            </Box>

            <TextField
              select
              fullWidth
              label="Status"
              value={editedRequest.status || 'pending'}
              onChange={(e) => setEditedRequest({ ...editedRequest, status: e.target.value as any })}
            >
              <MenuItem value="pending">Pending</MenuItem>
              <MenuItem value="in_review">In Review</MenuItem>
              <MenuItem value="completed">Completed</MenuItem>
              <MenuItem value="cancelled">Cancelled</MenuItem>
            </TextField>

            <TextField
              fullWidth
              multiline
              rows={4}
              label="Admin Notes"
              value={editedRequest.admin_notes || ''}
              onChange={(e) => setEditedRequest({ ...editedRequest, admin_notes: e.target.value })}
              placeholder="Add notes about this deletion request (e.g., date processed, confirmation sent, etc.)"
            />

            {editedRequest.status === 'completed' && (
              <Alert severity="warning">
                <strong>Important:</strong> Ensure the following have been completed:
                <ul style={{ marginBottom: 0, paddingLeft: 20 }}>
                  <li>User data deleted from Supabase (auth.users, profiles, user_reports, etc.)</li>
                  <li>User recipes and family data removed</li>
                  <li>Confirmation email sent to user</li>
                  <li>30-day retention period observed (if applicable)</li>
                </ul>
              </Alert>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSelectedRequest(null)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={updateMutation.isPending}
          >
            {updateMutation.isPending ? 'Saving...' : 'Save Changes'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
